import { useForm, useStore } from "@tanstack/react-form";
import { useTranslation } from "react-i18next";
import { Icon } from "@core/assets";
import {
  Button,
  DateField,
  Dialog,
  NumberField,
  Select,
  TextField,
} from "@core/components";
import { useIdempotencyKey } from "@core/hooks";
import { fieldErrorKey } from "@core/utils";
import { useAdministrators, useCreateCohort } from "../hooks";
import { classifyCreateFailure } from "../services/failure";
import { CreateCohortFormSchema } from "../types";
import type { Cohort } from "../../_shared/types";

interface CreateCohortDialogProps {
  /** Closes the dialog (Annuler, the cross, Escape). */
  onClose: () => void;
  /** Called with the new cohort after the server created it; the caller opens its page. */
  onCreated: (cohort: Cohort) => void;
  /** « Inviter un administrateur »: leads to the team page when there is nobody to choose. */
  onInviteAdministrator: () => void;
}

type FormErrorKey =
  | "create.errors.name-required"
  | "create.errors.places-invalid"
  | "create.errors.waitlist-invalid"
  | "create.errors.start-required"
  | "create.errors.end-required"
  | "create.errors.end-before-start"
  | "create.errors.administrator-required";

const FORM_ID = "create-cohort-form";

/**
 * « Nouvelle cohorte » (Figma "Create cohort" and its list, no-administrator and error frames): the
 * name, the places and the waiting list, the programme dates and the responsible Administrator.
 * « Créer la cohorte » stays greyed out until an Administrator is chosen. With no Administrator to
 * choose, the field reads « Aucun administrateur » and « Inviter un administrateur » leads to the
 * team page.
 *
 * Mounted only while open, so every opening starts clean. The same idempotency key is kept while
 * the values stay the same, so a retry after an uncertain answer never creates two cohorts; it
 * changes when a value does, and after a refusal.
 */
export function CreateCohortDialog({
  onClose,
  onCreated,
  onInviteAdministrator,
}: CreateCohortDialogProps) {
  const { t } = useTranslation("cohorts");
  const create = useCreateCohort();
  const administrators = useAdministrators();

  const form = useForm({
    defaultValues: {
      name: "",
      places: Number.NaN,
      waitlistPlaces: 0,
      startDate: "",
      endDate: "",
      administratorId: "",
    },
    validators: { onSubmit: CreateCohortFormSchema },
    onSubmit: ({ value }) => {
      create.mutate(
        { values: value, idempotencyKey: key },
        {
          onSuccess: onCreated,
          onError: () => {
            // A definitive refusal is a finished command: the next attempt is a new one.
            rotate();
          },
        }
      );
    },
  });

  const values = useStore(form.store, (state) => state.values);
  const { key, rotate } = useIdempotencyKey(JSON.stringify(values));

  const failure = create.error ? classifyCreateFailure(create.error) : null;
  // A refusal belongs to the values it was made for: editing anything clears it.
  const failureApplies =
    failure !== null &&
    create.variables !== undefined &&
    JSON.stringify(create.variables.values) === JSON.stringify(values);

  const serverError = (kind: string): boolean =>
    failureApplies && failure.kind === kind;

  const generalError = !failureApplies
    ? null
    : failure.kind === "rate_limited"
      ? t("create.errors.rate-limited")
      : failure.kind === "network"
        ? t("create.errors.network")
        : failure.kind === "forbidden"
          ? t("create.errors.forbidden")
          : failure.kind === "unknown" || failure.kind === "key_conflict"
            ? t("create.errors.unknown")
            : null;

  const options = (administrators.data ?? []).map((admin) => ({
    id: admin.id,
    label: admin.display_name,
    person: { email: admin.email },
  }));
  const hasNoAdministrator = administrators.isSuccess && options.length === 0;
  const isBusy = create.isPending;

  return (
    <Dialog
      isOpen
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      title={t("create.title")}
      hasCloseButton
      className="max-w-130"
      actions={
        <>
          <Button variant="secondary" onPress={onClose} isDisabled={isBusy}>
            {t("create.cancel")}
          </Button>
          <Button
            type="submit"
            form={FORM_ID}
            isPending={isBusy}
            isDisabled={isBusy || values.administratorId === ""}
          >
            {t("create.submit")}
          </Button>
        </>
      }
    >
      <form
        id={FORM_ID}
        className="flex flex-col gap-md"
        onSubmit={(event) => {
          event.preventDefault();
          event.stopPropagation();
          void form.handleSubmit();
        }}
        noValidate
      >
        <form.Field name="name">
          {(field) => {
            const errorKey = fieldErrorKey<FormErrorKey>(
              field.state.meta.errors
            );
            return (
              <TextField
                label={t("create.name")}
                autoComplete="off"
                value={field.state.value}
                onChange={(value) => field.handleChange(value)}
                onBlur={field.handleBlur}
                isDisabled={isBusy}
                errorMessage={
                  errorKey
                    ? t(errorKey)
                    : serverError("name_invalid")
                      ? t("create.errors.name-required")
                      : undefined
                }
              />
            );
          }}
        </form.Field>

        <div className="grid grid-cols-2 gap-md">
          <form.Field name="places">
            {(field) => {
              const errorKey = fieldErrorKey<FormErrorKey>(
                field.state.meta.errors
              );
              return (
                <NumberField
                  label={t("create.places")}
                  minValue={0}
                  value={field.state.value}
                  onChange={(value) => field.handleChange(value)}
                  onBlur={field.handleBlur}
                  isDisabled={isBusy}
                  errorMessage={errorKey ? t(errorKey) : undefined}
                />
              );
            }}
          </form.Field>
          <form.Field name="waitlistPlaces">
            {(field) => {
              const errorKey = fieldErrorKey<FormErrorKey>(
                field.state.meta.errors
              );
              return (
                <NumberField
                  label={t("create.waitlist")}
                  minValue={0}
                  value={field.state.value}
                  onChange={(value) => field.handleChange(value)}
                  onBlur={field.handleBlur}
                  isDisabled={isBusy}
                  errorMessage={errorKey ? t(errorKey) : undefined}
                />
              );
            }}
          </form.Field>
        </div>

        <div className="grid grid-cols-1 gap-md sm:grid-cols-2">
          <form.Field name="startDate">
            {(field) => {
              const errorKey = fieldErrorKey<FormErrorKey>(
                field.state.meta.errors
              );
              return (
                <DateField
                  label={t("create.start")}
                  value={field.state.value}
                  onChange={(value) => field.handleChange(value)}
                  onBlur={field.handleBlur}
                  isDisabled={isBusy}
                  errorMessage={errorKey ? t(errorKey) : undefined}
                />
              );
            }}
          </form.Field>
          <form.Field name="endDate">
            {(field) => {
              const errorKey = fieldErrorKey<FormErrorKey>(
                field.state.meta.errors
              );
              return (
                <DateField
                  label={t("create.end")}
                  value={field.state.value}
                  onChange={(value) => field.handleChange(value)}
                  onBlur={field.handleBlur}
                  isDisabled={isBusy}
                  errorMessage={
                    errorKey
                      ? t(errorKey)
                      : serverError("end_before_start")
                        ? t("create.errors.end-before-start")
                        : undefined
                  }
                />
              );
            }}
          </form.Field>
        </div>

        <form.Field name="administratorId">
          {(field) => (
            <Select
              label={t("create.administrator")}
              options={options}
              placeholder={
                hasNoAdministrator
                  ? t("create.no-administrator")
                  : t("create.administrator-placeholder")
              }
              selectedKey={field.state.value || null}
              onSelectionChange={(selected) =>
                field.handleChange(selected === null ? "" : String(selected))
              }
              isDisabled={isBusy || hasNoAdministrator}
              errorMessage={
                serverError("administrator_unavailable")
                  ? t("create.errors.administrator-unavailable")
                  : undefined
              }
            />
          )}
        </form.Field>

        {hasNoAdministrator ? (
          <div>
            <Button variant="tertiary" onPress={onInviteAdministrator}>
              <Icon name="user-plus" size={16} />
              {t("create.invite-administrator")}
            </Button>
          </div>
        ) : null}

        {administrators.isError ? (
          <p role="alert" className="text-sm-medium text-(--text-error)">
            {t("create.errors.administrators-load")}
          </p>
        ) : null}

        {generalError ? (
          <p role="alert" className="text-sm-medium text-(--text-error)">
            {generalError}
          </p>
        ) : null}
      </form>
    </Dialog>
  );
}
